import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { htmlToText } from '@/lib/display';

interface FormRow {
  id: number;
  title: string | null;
  description: string | null;
  coverimage: string | null;
}

/**
 * Balise {{default_form}} — affiche le formulaire par défaut du commerçant
 * (le premier formulaire qu'il a créé). Le client est renvoyé vers la page
 * publique de ce formulaire. Aucun formulaire = bloc masqué.
 */
export default function DefaultFormTag() {
  const { tenant, isTenant } = useTenant();
  const merchantId = tenant?.id ?? null;

  const [form, setForm] = useState<FormRow | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isTenant || !merchantId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await supabase
          .from('forms')
          .select('id, title, description, coverimage')
          .eq('commerceid', merchantId)
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();
        if (!cancelled) setForm((data as FormRow) || null);
      } catch {
        /* silencieux */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isTenant, merchantId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i>
      </div>
    );
  }

  if (!form) return null;

  const description = htmlToText(form.description) || '';

  return (
    <section className="w-full px-4 md:px-6 py-10">
      <div className="max-w-5xl mx-auto">
        <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2">
            {form.coverimage && (
              <div className="h-56 md:h-full bg-background-100">
                <img src={form.coverimage} alt={form.title || 'Formulaire'} className="w-full h-full object-cover object-top" />
              </div>
            )}
            <div className={`p-6 md:p-8 flex flex-col justify-center ${form.coverimage ? '' : 'md:col-span-2'}`}>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary-50 text-secondary-700 text-xs font-medium mb-3 w-fit">
                <i className="ri-survey-line"></i>Formulaire
              </span>
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-2">
                {form.title || 'Formulaire'}
              </h2>
              {description && (
                <p className="text-sm text-foreground-600 leading-relaxed mb-5 line-clamp-4">{description}</p>
              )}
              <Link
                to={`/forms/${form.id}`}
                className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors no-underline whitespace-nowrap w-fit"
              >
                <i className="ri-edit-2-line"></i>Remplir le formulaire
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}